import React, { useMemo, useState } from "react";

import { ConcernFieldTarget } from "./concerns";
import {
  NutrientDef,
  nutrientDefById,
  searchNutrients,
} from "./nutrientCatalog";
import { getEffectiveQuantity } from "./nutrition";
import { TagEditor } from "./shared";
import { ALL_TAGS } from "./tagCatalog";
import { PassioFood, PassioNutrient, PassioPortion } from "./types";

/**
 * Full add/edit editor for a Passio food record. Controlled: it never mutates
 * `value` in place — every change calls `onChange` with a new object so the
 * parent owns the draft + save/cancel.
 *
 * Covers every editable field of the record: identity, branded/product info,
 * tags, quality, web links, portions, concerns, and **nutrients** (per 100g)
 * via a type-to-search picker backed by the Passio nutrient catalog.
 */
export interface FoodEditorProps {
  value: PassioFood;
  onChange: (next: PassioFood) => void;
  /** Editor areas to visually flag (e.g. fields a blocking concern points at). */
  highlightFields?: Set<ConcernFieldTarget>;
}

const labelCls = "block text-xs font-medium text-gray-700";
const inputCls =
  "mt-1 w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm focus:border-indigo-500 focus:outline-none";
const sectionCls = "rounded-md border border-gray-200 bg-white p-3";
const sectionTitleCls =
  "text-xs font-semibold uppercase tracking-wide text-gray-500";

const round2 = (n: number): number => Math.round(n * 100) / 100;

const num = (v: string): number | undefined => {
  const n = parseFloat(v);
  return Number.isNaN(n) ? undefined : n;
};

const Field: React.FC<{
  label: string;
  children: React.ReactNode;
}> = ({ label, children }) => (
  <label className={labelCls}>
    {label}
    {children}
  </label>
);

// ----- nutrient type-to-search picker -----

const NutrientPicker: React.FC<{
  existingIds: Set<number>;
  onAdd: (def: NutrientDef) => void;
}> = ({ existingIds, onAdd }) => {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const matches = useMemo(() => searchNutrients(q, 12), [q]);

  return (
    <div className="relative">
      <input
        value={q}
        placeholder="Add nutrient — type to search (e.g. fiber, sodium, vitamin C)…"
        className={inputCls}
        onChange={(e) => {
          setQ(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
      />
      {open && matches.length > 0 && (
        <ul className="absolute z-20 mt-1 max-h-56 w-full overflow-auto rounded-md border border-gray-200 bg-white py-1 shadow-lg">
          {matches.map((m) => {
            const already = existingIds.has(m.id);
            return (
              <li key={m.id}>
                <button
                  type="button"
                  disabled={already}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => {
                    if (already) return;
                    onAdd(m);
                    setQ("");
                    setOpen(false);
                  }}
                  className={`flex w-full items-center justify-between px-3 py-1.5 text-left text-sm ${
                    already
                      ? "cursor-not-allowed text-gray-300"
                      : "text-gray-700 hover:bg-indigo-50"
                  }`}
                >
                  <span>
                    {m.name}
                    {m.shortName ? (
                      <span className="ml-1 text-xs text-gray-400">
                        {m.shortName}
                      </span>
                    ) : null}
                  </span>
                  <span className="text-xs text-gray-400">
                    {already ? "added" : m.unit.toLowerCase()}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export const FoodEditor: React.FC<FoodEditorProps> = ({
  value,
  onChange,
  highlightFields,
}) => {
  const patch = (p: Partial<PassioFood>) => onChange({ ...value, ...p });

  const branded = value.branded || {};
  const patchBranded = (p: Partial<typeof branded>) =>
    patch({ branded: { ...branded, ...p } });

  const nutrients: PassioNutrient[] = Array.isArray(value.nutrients)
    ? value.nutrients
    : [];
  const portions: PassioPortion[] = Array.isArray(value.portions)
    ? value.portions
    : [];
  const webLinks: string[] = Array.isArray(value.webLinks)
    ? (value.webLinks as string[])
    : [];

  // Section highlight: ring + "fix this" hint when a blocking concern points here.
  const hi = (target: ConcernFieldTarget): boolean =>
    !!highlightFields?.has(target);
  const secCls = (target: ConcernFieldTarget): string =>
    `${sectionCls}${hi(target) ? " ring-2 ring-amber-400 ring-offset-1" : ""}`;
  const FixHint: React.FC<{ show: boolean }> = ({ show }) =>
    show ? (
      <span className="ml-2 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-800">
        Fix to ingest
      </span>
    ) : null;

  // ----- nutrient edit basis: per-100g (stored) or per a chosen portion -----
  // Nutrients are ALWAYS stored per 100g. This lets the user type values as they
  // appear on a nutrition label (per serving); we convert back to per-100g live.
  const [basisIdx, setBasisIdx] = useState(-1); // -1 = per 100 g
  const basisPortion = basisIdx >= 0 ? portions[basisIdx] : undefined;
  const basisGrams =
    basisPortion && basisPortion.weight?.value
      ? basisPortion.weight.value * getEffectiveQuantity(basisPortion)
      : 100;
  // displayed = per100g * factor ; per100g = displayed / factor
  const factor = basisGrams / 100;
  const basisLabel =
    basisIdx >= 0 && basisPortion
      ? `${basisPortion.name || "portion"} (${round2(basisGrams)} g)`
      : "100 g";

  const existingNutrientIds = new Set(
    nutrients
      .map((n) => (typeof n.id === "number" ? n.id : Number(n.id)))
      .filter((n) => !Number.isNaN(n))
  );

  // ----- nutrients -----
  const addNutrient = (def: NutrientDef) =>
    patch({
      nutrients: [
        ...nutrients,
        {
          id: def.id,
          amount: 0,
          nutrient: {
            name: def.name,
            unit: def.unit,
            shortName: def.shortName,
          },
        },
      ],
    });
  const setNutrientAmount = (i: number, amount: number | undefined) =>
    patch({
      nutrients: nutrients.map((n, idx) =>
        idx === i ? { ...n, amount: amount ?? 0 } : n
      ),
    });
  const removeNutrient = (i: number) =>
    patch({ nutrients: nutrients.filter((_, idx) => idx !== i) });

  // ----- portions -----
  const addPortion = () =>
    patch({
      portions: [
        ...portions,
        { name: "serving", weight: { unit: "g", value: 0 }, quantity: 1 },
      ],
    });
  const patchPortion = (i: number, p: Partial<PassioPortion>) =>
    patch({
      portions: portions.map((pt, idx) => (idx === i ? { ...pt, ...p } : pt)),
    });
  const removePortion = (i: number) =>
    patch({ portions: portions.filter((_, idx) => idx !== i) });

  // ----- web links -----
  const setWebLink = (i: number, v: string) =>
    patch({ webLinks: webLinks.map((l, idx) => (idx === i ? v : l)) });
  const addWebLink = () => patch({ webLinks: [...webLinks, ""] });
  const removeWebLink = (i: number) =>
    patch({ webLinks: webLinks.filter((_, idx) => idx !== i) });

  return (
    <div className="space-y-3">
      {/* Identity */}
      <div className={secCls("identity")}>
        <div className={sectionTitleCls}>
          Identity
          <FixHint show={hi("identity")} />
        </div>
        <div className="mt-2 grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field label="Name">
            <input
              className={inputCls}
              value={value.name || ""}
              onChange={(e) => patch({ name: e.target.value })}
            />
          </Field>
          <Field label="Brand name">
            <input
              className={inputCls}
              value={value.brandName || ""}
              onChange={(e) => patch({ brandName: e.target.value })}
            />
          </Field>
        </div>
      </div>

      {/* Branded / product info */}
      <div
        className={`${sectionCls}${
          hi("brand") || hi("ingredients")
            ? " ring-2 ring-amber-400 ring-offset-1"
            : ""
        }`}
      >
        <div className={sectionTitleCls}>
          Product information
          <FixHint show={hi("brand") || hi("ingredients")} />
        </div>
        <div className="mt-2 grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field label="Brand owner">
            <input
              className={inputCls}
              value={branded.owner || ""}
              onChange={(e) => patchBranded({ owner: e.target.value })}
            />
          </Field>
          <Field label="UPC / product code">
            <input
              className={inputCls}
              value={branded.productCode || branded.upc || ""}
              placeholder="e.g. 850002251234"
              onChange={(e) => patchBranded({ productCode: e.target.value })}
            />
          </Field>
          <Field label="Country">
            <input
              className={inputCls}
              value={branded.country || ""}
              placeholder="e.g. US"
              onChange={(e) => patchBranded({ country: e.target.value })}
            />
          </Field>
          <Field label="Quality score">
            <input
              className={inputCls}
              value={value.qualityScore ? String(value.qualityScore) : ""}
              placeholder="e.g. High / Medium / Low"
              onChange={(e) => patch({ qualityScore: e.target.value })}
            />
          </Field>
        </div>
        <div className="mt-3">
          <Field label="Ingredients">
            <textarea
              className={inputCls}
              rows={3}
              value={branded.ingredients || ""}
              onChange={(e) => patchBranded({ ingredients: e.target.value })}
            />
          </Field>
        </div>
      </div>

      {/* Tags */}
      <div className={sectionCls}>
        <div className={sectionTitleCls}>Tags</div>
        <p className="mt-1 text-[11px] text-gray-400">
          Pick from the controlled list (food group + NOVA, diet/allergen
          flags). keto_*/upf_* scores are derived automatically on re-check.
        </p>
        <div className="mt-2">
          <TagEditor
            tags={Array.isArray(value.tags) ? value.tags : []}
            onChange={(tags) => patch({ tags })}
            allowed={ALL_TAGS}
          />
        </div>
      </div>

      {/* Nutrients (stored per 100g; editable per 100g or per a chosen portion) */}
      <div className={secCls("nutrients")}>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className={sectionTitleCls}>
            Nutrients
            <FixHint show={hi("nutrients")} />
          </div>
          <span className="text-[11px] text-gray-400">
            {nutrients.length} nutrient{nutrients.length === 1 ? "" : "s"}
          </span>
        </div>

        {/* Basis selector: type values per 100 g or as shown for a portion. */}
        <div className="mt-2 flex flex-wrap items-center gap-2 rounded-md bg-indigo-50/60 px-2 py-1.5">
          <label className="text-xs font-medium text-gray-600">
            Enter values per
          </label>
          <select
            value={basisIdx}
            onChange={(e) => setBasisIdx(Number(e.target.value))}
            className="rounded-md border border-gray-300 bg-white px-2 py-1 text-xs focus:border-indigo-500 focus:outline-none"
          >
            <option value={-1}>100 g (as stored)</option>
            {portions.map((p, i) => {
              const g = (p.weight?.value || 0) * getEffectiveQuantity(p);
              return (
                <option key={`${p.name}-${i}`} value={i}>
                  {p.name || "portion"} ({round2(g)} g)
                </option>
              );
            })}
          </select>
          {basisIdx >= 0 && (
            <span className="text-[11px] italic text-indigo-700">
              Editing per {basisLabel} — stored per 100 g automatically.
            </span>
          )}
        </div>

        <div className="mt-2 space-y-1.5">
          {nutrients.map((n, i) => {
            const def =
              nutrientDefById(typeof n.id === "number" ? n.id : Number(n.id)) ||
              undefined;
            const name = n.nutrient?.name || def?.name || `Nutrient ${n.id}`;
            const unit = n.nutrient?.unit || def?.unit || "";
            const per100 = typeof n.amount === "number" ? n.amount : undefined;
            const shown = per100 === undefined ? "" : round2(per100 * factor);
            return (
              <div
                key={`${n.id}-${i}`}
                className="flex items-center gap-2 rounded-md border border-gray-100 bg-gray-50 px-2 py-1.5"
              >
                <span className="flex-1 truncate text-xs text-gray-700">
                  {name}
                </span>
                <input
                  type="number"
                  className="w-24 rounded-md border border-gray-300 px-2 py-1 text-right text-sm focus:border-indigo-500 focus:outline-none"
                  value={shown}
                  step="any"
                  min={0}
                  onChange={(e) => {
                    const d = num(e.target.value);
                    if (d === undefined) {
                      setNutrientAmount(i, undefined);
                      return;
                    }
                    // Convert the displayed (basis) value back to per-100g.
                    setNutrientAmount(i, factor > 0 ? round2(d / factor) : d);
                  }}
                />
                <span className="w-12 text-xs text-gray-400">
                  {unit.toLowerCase()}
                </span>
                <button
                  type="button"
                  onClick={() => removeNutrient(i)}
                  className="rounded p-1 text-gray-400 hover:bg-red-50 hover:text-red-600"
                  aria-label={`Remove ${name}`}
                >
                  ×
                </button>
              </div>
            );
          })}
          {nutrients.length === 0 && (
            <div className="text-xs text-gray-400">
              No nutrients yet — add them below.
            </div>
          )}
        </div>
        <div className="mt-2">
          <NutrientPicker
            existingIds={existingNutrientIds}
            onAdd={addNutrient}
          />
        </div>
      </div>

      {/* Portions */}
      <div className={secCls("portions")}>
        <div className="flex items-center justify-between">
          <div className={sectionTitleCls}>
            Portions
            <FixHint show={hi("portions")} />
          </div>
          <button
            type="button"
            onClick={addPortion}
            className="text-xs font-semibold text-indigo-600 hover:text-indigo-800"
          >
            + Add portion
          </button>
        </div>
        <div className="mt-2 space-y-2">
          {portions.map((p, i) => (
            <div
              key={i}
              className="grid grid-cols-2 gap-2 rounded-md border border-gray-100 bg-gray-50 p-2 sm:grid-cols-4"
            >
              <Field label="Name">
                <input
                  className={inputCls}
                  value={p.name || ""}
                  onChange={(e) => patchPortion(i, { name: e.target.value })}
                />
              </Field>
              <Field label="Weight value">
                <input
                  type="number"
                  step="any"
                  className={inputCls}
                  value={
                    typeof p.weight?.value === "number" ? p.weight.value : ""
                  }
                  onChange={(e) =>
                    patchPortion(i, {
                      weight: {
                        ...(p.weight || {}),
                        value: num(e.target.value),
                      },
                    })
                  }
                />
              </Field>
              <Field label="Unit">
                <input
                  className={inputCls}
                  value={p.weight?.unit || "g"}
                  onChange={(e) =>
                    patchPortion(i, {
                      weight: { ...(p.weight || {}), unit: e.target.value },
                    })
                  }
                />
              </Field>
              <div className="flex items-end gap-2">
                <Field label="Default qty">
                  <input
                    type="number"
                    step="any"
                    className={inputCls}
                    value={typeof p.quantity === "number" ? p.quantity : ""}
                    onChange={(e) =>
                      patchPortion(i, { quantity: num(e.target.value) })
                    }
                  />
                </Field>
                <button
                  type="button"
                  onClick={() => removePortion(i)}
                  className="mb-1 rounded p-1.5 text-gray-400 hover:bg-red-50 hover:text-red-600"
                  aria-label="Remove portion"
                >
                  ×
                </button>
              </div>
            </div>
          ))}
          {portions.length === 0 && (
            <div className="text-xs text-gray-400">No portions yet.</div>
          )}
        </div>
      </div>

      {/* Web links */}
      <div className={sectionCls}>
        <div className="flex items-center justify-between">
          <div className={sectionTitleCls}>Web links</div>
          <button
            type="button"
            onClick={addWebLink}
            className="text-xs font-semibold text-indigo-600 hover:text-indigo-800"
          >
            + Add link
          </button>
        </div>
        <div className="mt-2 space-y-2">
          {webLinks.map((l, i) => (
            <div key={i} className="flex items-center gap-2">
              <input
                className={inputCls}
                value={l}
                placeholder="https://…"
                onChange={(e) => setWebLink(i, e.target.value)}
              />
              <button
                type="button"
                onClick={() => removeWebLink(i)}
                className="rounded p-1.5 text-gray-400 hover:bg-red-50 hover:text-red-600"
                aria-label="Remove link"
              >
                ×
              </button>
            </div>
          ))}
          {webLinks.length === 0 && (
            <div className="text-xs text-gray-400">No web links.</div>
          )}
        </div>
      </div>

      {/* Concerns are auto-applied by the hardcoded sanity checks and are not
          hand-editable; use "Re-check concerns" after editing to recompute. */}
      <div className="rounded-md border border-dashed border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-500">
        Concern codes are applied automatically by the sanity checks. Edit the
        data above, then re-check to recompute them.
      </div>
    </div>
  );
};

export default FoodEditor;
